import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Checkbox,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import SortIcon from "@mui/icons-material/Sort";
import {
  ACTIVE_STATUSES,
  ATTENTION_NEEDED_STATUSES,
  ALL_STATUSES,
  TERMINAL_STATUSES,
  SEVERITY_META,
  getStatusLabel,
} from "../common/statusColors";
import {
  filterActiveChipSx,
  filterCaptionSx,
  filterListItemSx,
  filterListItemTextProps,
  filterPopoverPaperSx,
  filterPresetChipSx,
  filterScrollListSx,
  filterSearchFieldSx,
  filterSearchInputSx,
  filterTriggerSx,
  FILTER_POPOVER_WIDTH,
} from "../common/filterBarStyles";

const SEVERITIES = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];

const SORT_OPTIONS = [
  { value: "urgency:desc", label: "Most urgent" },
  { value: "created_at:desc", label: "Newest" },
  { value: "created_at:asc", label: "Oldest" },
  { value: "scheduled_at:asc", label: "Scheduled soonest" },
  { value: "severity:desc", label: "Highest severity" },
  { value: "check_title:asc", label: "Check name (A→Z)" },
];

const STATUS_PRESETS = [
  { key: "attention", label: "Needs attention" },
  { key: "active", label: "Active" },
  { key: "terminal", label: "Terminal" },
];

const FilterTrigger = ({ label, count, icon, onClick }) => (
  <Button
    variant="outlined"
    size="small"
    startIcon={icon}
    onClick={onClick}
    sx={filterTriggerSx}
  >
    {label}
    {count != null && count > 0 ? ` (${count})` : ""}
  </Button>
);

const FilterPopover = ({ open, anchorEl, onClose, width = FILTER_POPOVER_WIDTH, children }) => (
  <Popover
    open={open}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    transformOrigin={{ vertical: "top", horizontal: "left" }}
    slotProps={{ paper: { sx: filterPopoverPaperSx(width) } }}
  >
    {children}
  </Popover>
);

const SingleSelectRow = ({ label, selected, onClick, adornment }) => (
  <ListItemButton dense selected={selected} onClick={onClick} sx={filterListItemSx}>
    {adornment}
    <ListItemText primary={label} primaryTypographyProps={filterListItemTextProps} sx={{ m: 0 }} />
    {selected && <CheckIcon sx={{ fontSize: 16, color: "primary.main", ml: "auto" }} />}
  </ListItemButton>
);

const SeverityDot = ({ severity }) => {
  const meta = SEVERITY_META[severity] || { fg: "#9e9e9e" };
  return (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: meta.fg,
        flexShrink: 0,
        mr: 1,
      }}
    />
  );
};

/**
 * Top-of-list filter and search bar. Filter chips map directly to API query
 * params; selecting a chip mutates the parent's filter state which is then
 * synced into the URL so the view is shareable.
 */
const FilterBar = ({
  filters,
  onChange,
  onSearchChange,
  onReset,
  searchValue = "",
  onSearchSubmit,
  onNewRequest,
  remediableCheckIds = [],
  accountOptions = [],
  accountsLoading = false,
  serviceOptions = [],
  onRefresh,
  refreshing,
}) => {
  const [statusAnchor, setStatusAnchor] = useState(null);
  const [statusQuery, setStatusQuery] = useState("");
  const [severityAnchor, setSeverityAnchor] = useState(null);
  const [serviceAnchor, setServiceAnchor] = useState(null);
  const [accountAnchor, setAccountAnchor] = useState(null);
  const [sortAnchor, setSortAnchor] = useState(null);

  const closeStatusPopover = () => {
    setStatusAnchor(null);
    setStatusQuery("");
  };

  const filteredStatuses = useMemo(() => {
    const q = statusQuery.trim().toLowerCase();
    if (!q) return ALL_STATUSES;
    return ALL_STATUSES.filter(
      (s) => getStatusLabel(s).toLowerCase().includes(q) || s.toLowerCase().includes(q)
    );
  }, [statusQuery]);

  const statusSet = new Set((filters.status_in || "").split(",").filter(Boolean));
  const toggleStatus = (status) => {
    const next = new Set(statusSet);
    if (next.has(status)) next.delete(status);
    else next.add(status);
    onChange({ status_in: Array.from(next).join(",") || null });
  };

  const applyStatusPreset = (preset) => {
    if (preset !== "clear" && isStatusPresetSelected(preset)) {
      onChange({ status_in: null });
      return;
    }
    if (preset === "attention") onChange({ status_in: ATTENTION_NEEDED_STATUSES.join(",") });
    else if (preset === "active") onChange({ status_in: ACTIVE_STATUSES.join(",") });
    else if (preset === "terminal") onChange({ status_in: Array.from(TERMINAL_STATUSES).join(",") });
    else onChange({ status_in: null });
  };

  const isStatusPresetSelected = (key) => {
    const preset =
      key === "attention"
        ? ATTENTION_NEEDED_STATUSES
        : key === "active"
          ? ACTIVE_STATUSES
          : key === "terminal"
            ? Array.from(TERMINAL_STATUSES)
            : null;
    if (!preset || statusSet.size !== preset.length) return false;
    return preset.every((status) => statusSet.has(status));
  };

  const sortLabel =
    SORT_OPTIONS.find((o) => o.value === (filters.sort || "urgency:desc"))?.label || "Most urgent";

  const activeFilterChips = [];
  if (filters.status_in)
    activeFilterChips.push(
      ...filters.status_in.split(",").filter(Boolean).map((s) => ({
        key: `status:${s}`,
        label: `Status: ${getStatusLabel(s)}`,
        clear: () => toggleStatus(s),
      }))
    );
  if (filters.severity)
    activeFilterChips.push({
      key: "severity",
      label: `Severity: ${filters.severity}`,
      clear: () => onChange({ severity: null }),
    });
  if (filters.service)
    activeFilterChips.push({
      key: "service",
      label: `Service: ${filters.service}`,
      clear: () => onChange({ service: null }),
    });
  if (filters.account)
    activeFilterChips.push({
      key: "account",
      label: `Account: ${filters.account}`,
      clear: () => onChange({ account: null }),
    });
  if (filters.check)
    activeFilterChips.push({
      key: "check",
      label: `Check: ${filters.check}`,
      clear: () => onChange({ check: null }),
    });

  return (
    <Box sx={{ mb: 2, fontSize: 13 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
        <TextField
          size="small"
          placeholder="Search check, resource, requester…"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSearchSubmit?.();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
              </InputAdornment>
            ),
            endAdornment: searchValue ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    onSearchChange("");
                    onSearchSubmit?.("");
                  }}
                >
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={filterSearchFieldSx}
        />

        <FilterTrigger
          label="Status"
          count={statusSet.size}
          icon={<FilterListIcon sx={{ fontSize: 18 }} />}
          onClick={(e) => setStatusAnchor(e.currentTarget)}
        />
        <FilterPopover open={Boolean(statusAnchor)} anchorEl={statusAnchor} onClose={closeStatusPopover}>
          <Box sx={{ p: 1.25, pb: 1 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search status…"
              value={statusQuery}
              onChange={(e) => setStatusQuery(e.target.value)}
              autoFocus
              InputProps={{
                sx: filterSearchInputSx,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
              {STATUS_PRESETS.map((p) => {
                const selected = isStatusPresetSelected(p.key);
                return (
                  <Chip
                    key={p.key}
                    label={p.label}
                    size="small"
                    variant={selected ? "filled" : "outlined"}
                    color={selected ? "primary" : "default"}
                    onClick={() => applyStatusPreset(p.key)}
                    sx={filterPresetChipSx}
                  />
                );
              })}
            </Stack>
          </Box>
          <Divider />
          <Box sx={filterScrollListSx}>
            {filteredStatuses.length === 0 ? (
              <Typography sx={{ ...filterCaptionSx, px: 1.5, py: 1.5, display: "block" }}>
                No matching status
              </Typography>
            ) : (
              filteredStatuses.map((s) => (
                <ListItemButton
                  key={s}
                  dense
                  onClick={() => toggleStatus(s)}
                  sx={filterListItemSx}
                >
                  <Checkbox
                    size="small"
                    checked={statusSet.has(s)}
                    tabIndex={-1}
                    disableRipple
                    sx={{ p: 0.25, mr: 0.25 }}
                  />
                  <ListItemText
                    primary={getStatusLabel(s)}
                    primaryTypographyProps={filterListItemTextProps}
                  />
                </ListItemButton>
              ))
            )}
          </Box>
          {statusSet.size > 0 && (
            <>
              <Divider />
              <Box sx={{ px: 1.25, py: 0.75, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={filterCaptionSx}>{statusSet.size} selected</Typography>
                <Button
                  size="small"
                  onClick={() => applyStatusPreset("clear")}
                  sx={{ minWidth: 0, fontSize: 12, fontWeight: 500, py: 0, textTransform: "none" }}
                >
                  Clear all
                </Button>
              </Box>
            </>
          )}
        </FilterPopover>

        <FilterTrigger
          label={filters.severity ? `Severity: ${filters.severity}` : "Severity"}
          icon={<TuneIcon sx={{ fontSize: 18 }} />}
          onClick={(e) => setSeverityAnchor(e.currentTarget)}
        />
        <FilterPopover
          open={Boolean(severityAnchor)}
          anchorEl={severityAnchor}
          onClose={() => setSeverityAnchor(null)}
          width={220}
        >
          <Box sx={{ px: 0.5, py: 0.5 }}>
            <SingleSelectRow
              label="All severities"
              selected={!filters.severity}
              onClick={() => {
                onChange({ severity: null });
                setSeverityAnchor(null);
              }}
            />
            {SEVERITIES.map((s) => (
              <SingleSelectRow
                key={s}
                label={s}
                selected={filters.severity === s}
                adornment={<SeverityDot severity={s} />}
                onClick={() => {
                  onChange({ severity: s });
                  setSeverityAnchor(null);
                }}
              />
            ))}
          </Box>
        </FilterPopover>

        {serviceOptions.length > 0 && (
          <>
            <FilterTrigger
              label={filters.service ? `Service: ${filters.service}` : "Service"}
              icon={<TuneIcon sx={{ fontSize: 18 }} />}
              onClick={(e) => setServiceAnchor(e.currentTarget)}
            />
            <FilterPopover
              open={Boolean(serviceAnchor)}
              anchorEl={serviceAnchor}
              onClose={() => setServiceAnchor(null)}
              width={200}
            >
              <Box sx={{ px: 0.5, py: 0.5 }}>
                <SingleSelectRow
                  label="All services"
                  selected={!filters.service}
                  onClick={() => {
                    onChange({ service: null });
                    setServiceAnchor(null);
                  }}
                />
                {serviceOptions.map((s) => (
                  <SingleSelectRow
                    key={s}
                    label={s}
                    selected={filters.service === s}
                    onClick={() => {
                      onChange({ service: s });
                      setServiceAnchor(null);
                    }}
                  />
                ))}
              </Box>
            </FilterPopover>
          </>
        )}

        <>
          <FilterTrigger
            label={
              filters.account
                ? `Account: ${accountOptions.find((a) => a.value === filters.account)?.label || filters.account}`
                : "Account"
            }
            icon={<TuneIcon sx={{ fontSize: 18 }} />}
            onClick={(e) => setAccountAnchor(e.currentTarget)}
          />
          <FilterPopover
            open={Boolean(accountAnchor)}
            anchorEl={accountAnchor}
            onClose={() => setAccountAnchor(null)}
            width={FILTER_POPOVER_WIDTH}
          >
            {accountsLoading && accountOptions.length === 0 ? (
              <Typography sx={{ px: 2, py: 1.5, fontSize: 13, color: "text.secondary" }}>
                Loading accounts…
              </Typography>
            ) : accountOptions.length === 0 ? (
              <Typography sx={{ px: 2, py: 1.5, fontSize: 13, color: "text.secondary" }}>
                No accounts loaded. Open Configuration → Cloud Accounts, then return here.
              </Typography>
            ) : (
              <Box sx={{ ...filterScrollListSx, maxHeight: 240 }}>
                <SingleSelectRow
                  label="All accounts"
                  selected={!filters.account}
                  onClick={() => {
                    onChange({ account: null });
                    setAccountAnchor(null);
                  }}
                />
                {accountOptions.map((a) => (
                  <SingleSelectRow
                    key={a.value}
                    label={a.label}
                    selected={filters.account === a.value}
                    onClick={() => {
                      onChange({ account: a.value });
                      setAccountAnchor(null);
                    }}
                  />
                ))}
              </Box>
            )}
          </FilterPopover>
        </>

        <FilterTrigger
          label={`Sort: ${sortLabel}`}
          icon={<SortIcon sx={{ fontSize: 18 }} />}
          onClick={(e) => setSortAnchor(e.currentTarget)}
        />
        <FilterPopover
          open={Boolean(sortAnchor)}
          anchorEl={sortAnchor}
          onClose={() => setSortAnchor(null)}
          width={220}
        >
          <Box sx={{ px: 0.5, py: 0.5 }}>
            {SORT_OPTIONS.map((o) => (
              <SingleSelectRow
                key={o.value}
                label={o.label}
                selected={(filters.sort || "urgency:desc") === o.value}
                onClick={() => {
                  onChange({ sort: o.value });
                  setSortAnchor(null);
                }}
              />
            ))}
          </Box>
        </FilterPopover>

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title="Refresh now">
          <span>
            <IconButton onClick={onRefresh} disabled={refreshing} size="small">
              <RefreshIcon
                sx={{
                  fontSize: 20,
                  animation: refreshing ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    from: { transform: "rotate(0deg)" },
                    to: { transform: "rotate(360deg)" },
                  },
                }}
              />
            </IconButton>
          </span>
        </Tooltip>

        {onNewRequest && (
          <Button
            variant="contained"
            size="small"
            onClick={onNewRequest}
            sx={{ ...filterTriggerSx, backgroundColor: "primary.main", color: "primary.contrastText", "&:hover": { backgroundColor: "primary.dark" } }}
          >
            New request
          </Button>
        )}
      </Stack>

      {activeFilterChips.length > 0 && (
        <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
          {activeFilterChips.map((c) => (
            <Chip
              key={c.key}
              label={c.label}
              size="small"
              onDelete={c.clear}
              sx={filterActiveChipSx}
            />
          ))}
          <Button
            size="small"
            onClick={onReset}
            sx={{ ml: 0.5, fontSize: 12, fontWeight: 500, textTransform: "none", minHeight: 26 }}
          >
            Clear all
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default FilterBar;
